import { useState } from "react";

import { Button } from "@bokio/designsystem/components/Button";
import { TextAreaField } from "@bokio/designsystem/components/Form";
import { RenderRequestError } from "@bokio/designsystem/components/Loading";
import { Modal, ModalContent, ModalFooter } from "@bokio/designsystem/components/Modal";
import { Paragraph } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import { useDeviceQuery } from "@bokio/elements/DeviceQuery/useDeviceQuery";
import { GeneralLangFactory, SettingsLangFactory } from "@bokio/lang";

import { SensitiveInfoReminderLink } from "../SensitiveInfoReminderLink/SensitiveInfoReminderLink";

import type { Appearance } from "@bokio/designsystem/components/Button";
import type * as m from "@bokio/mobile-web-shared/core/model/model";
import type { RequestState } from "@bokio/mobile-web-shared/services/api/requestState";
import type { FC, ReactNode } from "react";

interface FeedbackModalProps {
	visible: boolean;
	onClose: () => void;
	onSendFeedback: (text: string) => void;
	request?: RequestState<m.Envelope<boolean | m.Settings.Controllers.HelpCreateResult, m.SimpleError>>;
	requests?: RequestState<m.Envelope<boolean | m.Settings.Controllers.HelpCreateResult, m.SimpleError>>[];
	text?: ReactNode;
	title?: string;
	textInputTitle?: string;
	primaryButtonText?: string;
	primaryButtonAppearance?: Appearance;
	allowEmptyMessage?: boolean;
}

export const FeedbackModal: FC<FeedbackModalProps> = ({
	visible,
	onClose,
	onSendFeedback,
	request,
	text,
	requests,
	title,
	textInputTitle,
	primaryButtonText,
	primaryButtonAppearance = "primary",
	allowEmptyMessage = false,
}) => {
	const [textValue, writeText] = useState("");
	const { isMobile } = useDeviceQuery();
	const generalLang = GeneralLangFactory();
	const settingsLang = SettingsLangFactory();
	const anyRequestLoading = request?.isLoading || requests?.some(x => x.isLoading);
	return (
		<Modal visible={visible} onClose={onClose} title={title ?? settingsLang.AddOns_Feedback_Modal_Title}>
			<ModalContent>
				{text && <Paragraph>{text}</Paragraph>}
				<TextAreaField
					onChange={writeText}
					label={textInputTitle ?? settingsLang.AddOns_Feedback_Modal_Message_Label}
					value={textValue}
					placeholder={settingsLang.AddOns_Feedback_Modal_Message_Placeholder}
					autoFocus={!isMobile && true}
					minRows={9}
					disabled={anyRequestLoading}
					testId="FeedbackModal_TextArea_input"
					hint={<SensitiveInfoReminderLink />}
				/>
			</ModalContent>
			<RenderRequestError request={request} requests={requests} />
			<ModalFooter>
				<Button
					appearance="secondary"
					onClick={onClose}
					disabled={anyRequestLoading}
					testId="FeedbackModal_Cancel_button"
				>
					{generalLang.Cancel}
				</Button>
				<Button
					disabled={!allowEmptyMessage && !textValue}
					appearance={primaryButtonAppearance}
					onClick={() => onSendFeedback(textValue)}
					loading={anyRequestLoading}
					testId="FeedbackModal_send_button"
				>
					{primaryButtonText ?? generalLang.Submit}
				</Button>
			</ModalFooter>
		</Modal>
	);
};
